import { createLogger } from '@/debug/logger'

const THUMBNAIL_DB_NAME = 'huebo:thumbnail-cache'
const THUMBNAIL_DB_VERSION = 1
const THUMBNAIL_STORE_NAME = 'thumbnails'
const LEGACY_THUMB_PREFIX = 'huebo:scenes:thumb:'
const MAX_MEMORY_THUMBNAIL_URLS = 128
const MAX_PERSISTED_THUMBNAIL_BYTES = 64 * 1024 * 1024
const MAX_PERSISTED_THUMBNAIL_ENTRIES = 512
/**
 * A replaced or trimmed object URL is revoked this much later, not at once: an <img> may still show
 * it until its component has the new URL (revoking it at once left a broken-image icon).
 */
const REVOKE_DELAY_MS = 30_000

type ThumbnailRecord = {
  id: string
  blob: Blob
  mimeType: string
  size: number
  createdAt: number
  accessedAt: number
}

type MemoryThumbnail = {
  url: string
  accessedAt: number
}

const log = createLogger('thumbnails')
const memoryThumbnails = new Map<string, MemoryThumbnail>()
/** One IndexedDB read per thumbnail at a time: concurrent reads each made a URL and revoked the other's. */
const loading = new Map<string, Promise<string | null>>()
let dbPromise: Promise<IDBDatabase> | null = null

export function getCachedThumbnailImage(id: string): string | null {
  const cached = memoryThumbnails.get(id)
  if (!cached) return null
  cached.accessedAt = Date.now()
  return cached.url
}

export function loadThumbnailImage(id: string): Promise<string | null> {
  const memoryHit = getCachedThumbnailImage(id)
  if (memoryHit) return Promise.resolve(memoryHit)
  if (!canUseIndexedDb()) return Promise.resolve(null)
  const inFlight = loading.get(id)
  if (inFlight) return inFlight
  const promise = readThumbnailImage(id).finally(() => loading.delete(id))
  loading.set(id, promise)
  return promise
}

async function readThumbnailImage(id: string): Promise<string | null> {
  const db = await openThumbnailDb()
  const tx = db.transaction(THUMBNAIL_STORE_NAME, 'readwrite')
  const store = tx.objectStore(THUMBNAIL_STORE_NAME)
  const record = await requestToPromise(store.get(id) as IDBRequest<ThumbnailRecord | undefined>)
  if (!record) {
    await transactionDone(tx)
    return null
  }

  const now = Date.now()
  store.put({ ...record, accessedAt: now } satisfies ThumbnailRecord)
  await transactionDone(tx)
  // A new capture may have landed meanwhile (setThumbnailImage): it is newer than this record.
  const fresher = getCachedThumbnailImage(id)
  if (fresher) return fresher
  return rememberThumbnailUrl(id, record.blob, now)
}

export async function setThumbnailImage(id: string, dataUrl: string): Promise<string> {
  const blob = await dataUrlToBlob(dataUrl)
  const now = Date.now()
  const url = rememberThumbnailUrl(id, blob, now)

  if (!canUseIndexedDb()) return url

  const db = await openThumbnailDb()
  const tx = db.transaction(THUMBNAIL_STORE_NAME, 'readwrite')
  tx.objectStore(THUMBNAIL_STORE_NAME).put({
    id,
    blob,
    mimeType: blob.type || 'image/png',
    size: blob.size,
    createdAt: now,
    accessedAt: now,
  } satisfies ThumbnailRecord)
  await transactionDone(tx)
  await prunePersistedThumbnails()
  return url
}

export async function deleteThumbnailImage(id: string): Promise<void> {
  forgetThumbnailUrl(id)
  if (!canUseIndexedDb()) return

  const db = await openThumbnailDb()
  const tx = db.transaction(THUMBNAIL_STORE_NAME, 'readwrite')
  tx.objectStore(THUMBNAIL_STORE_NAME).delete(id)
  await transactionDone(tx)
}

export function purgeLegacyLocalStorageThumbnailImages(storage: Storage | undefined = globalThis.localStorage): void {
  if (!storage) return
  for (let index = storage.length - 1; index >= 0; index--) {
    const key = storage.key(index)
    if (key?.startsWith(LEGACY_THUMB_PREFIX)) {
      storage.removeItem(key)
    }
  }
}

function canUseIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined'
    && typeof Blob !== 'undefined'
    && typeof URL !== 'undefined'
    && typeof URL.createObjectURL === 'function'
}

function openThumbnailDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(THUMBNAIL_DB_NAME, THUMBNAIL_DB_VERSION)

      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(THUMBNAIL_STORE_NAME)) {
          const store = db.createObjectStore(THUMBNAIL_STORE_NAME, { keyPath: 'id' })
          store.createIndex('accessedAt', 'accessedAt')
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('Unable to open thumbnail IndexedDB'))
      request.onblocked = () => reject(new Error('Thumbnail IndexedDB upgrade blocked'))
    })
  }
  return dbPromise
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'))
  })
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl)
  return response.blob()
}

function rememberThumbnailUrl(id: string, blob: Blob, accessedAt: number): string {
  forgetThumbnailUrl(id)
  const url = URL.createObjectURL(blob)
  memoryThumbnails.set(id, { url, accessedAt })
  trimMemoryThumbnails()
  return url
}

function forgetThumbnailUrl(id: string): void {
  const cached = memoryThumbnails.get(id)
  if (!cached) return
  memoryThumbnails.delete(id)
  setTimeout(() => URL.revokeObjectURL(cached.url), REVOKE_DELAY_MS)
}

function trimMemoryThumbnails(): void {
  while (memoryThumbnails.size > MAX_MEMORY_THUMBNAIL_URLS) {
    let oldestId: string | null = null
    let oldestAccessedAt = Infinity
    for (const [id, cached] of memoryThumbnails) {
      if (cached.accessedAt < oldestAccessedAt) {
        oldestId = id
        oldestAccessedAt = cached.accessedAt
      }
    }
    if (oldestId === null) return
    forgetThumbnailUrl(oldestId)
  }
}

async function prunePersistedThumbnails(): Promise<void> {
  const db = await openThumbnailDb()
  const tx = db.transaction(THUMBNAIL_STORE_NAME, 'readwrite')
  const store = tx.objectStore(THUMBNAIL_STORE_NAME)
  const records = await requestToPromise(store.getAll() as IDBRequest<ThumbnailRecord[]>)
  records.sort((a, b) => a.accessedAt - b.accessedAt)

  let totalBytes = records.reduce((sum, record) => sum + record.size, 0)
  let entries = records.length
  for (const record of records) {
    if (entries <= MAX_PERSISTED_THUMBNAIL_ENTRIES && totalBytes <= MAX_PERSISTED_THUMBNAIL_BYTES) break
    store.delete(record.id)
    forgetThumbnailUrl(record.id)
    totalBytes -= record.size
    entries -= 1
  }

  await transactionDone(tx)
  if (log.debugEnabled()) {
    log.debug('pruned persistent thumbnail cache', { entries, totalBytes })
  }
}
